/**
 * What the clients need to open, close and paste into the account's computer, read off a
 * computer card's view. Shared so web and native open the same way, and so a
 * card without a route falls back to its summary on both.
 */

import type { ComputerDesktopArtifact } from "@/types/artifacts.ts";

/** The `openPath` of the card's button, or undefined outside a thread. */
export const computerDesktopOpenPath = (artifact: ComputerDesktopArtifact): string | undefined => {
  for (const component of artifact.view?.components ?? []) {
    if (component.type === "computerDesktop") return component.openPath;
  }
  return undefined;
};

/**
 * The route that revokes a desktop link, or undefined outside a thread and on a
 * server that predates it.
 */
export const computerDesktopClosePath = (artifact: ComputerDesktopArtifact): string | undefined => {
  for (const component of artifact.view?.components ?? []) {
    if (component.type === "computerDesktop") return component.closePath;
  }
  return undefined;
};

/**
 * The route that types the user's clipboard on the computer, or undefined
 * outside a thread and on a server that predates it.
 */
export const computerDesktopPastePath = (artifact: ComputerDesktopArtifact): string | undefined => {
  for (const component of artifact.view?.components ?? []) {
    if (component.type === "computerDesktop") return component.pastePath;
  }
  return undefined;
};

/** The desktop's viewer: its port in the first host label, then a signed token. */
const COMPUTER_DESKTOP_HOST = /^6080-[a-z0-9_-]+\.daytonaproxy01\.net$/i;

/**
 * Whether a link is the computer's desktop viewer, the only thing the web's
 * desktop overlay may frame.
 *
 * The link comes from our API, which reads it from the provider, and it is a
 * bearer capability for a machine that may hold the user's sign-ins. The frame
 * sits inside our own chrome under the title "My computer", so what it shows
 * borrows our name. The CSP's `frame-src` has to allow every host on the
 * provider's preview domain — the token is a host label, so it cannot be
 * narrower — and that domain serves every one of the provider's customers. This
 * check is what keeps the frame to the desktop port of a signed link: https,
 * the viewer's port, the preview domain, the viewer page.
 *
 * Web only. iOS hands the link to Safari, which has no frame to protect and
 * deliberately accepts a host this does not know.
 */
export function isComputerDesktopUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return (
    url.protocol === "https:" &&
    url.port === "" &&
    !url.username &&
    !url.password &&
    COMPUTER_DESKTOP_HOST.test(url.hostname) &&
    url.pathname === "/vnc.html"
  );
}
